/* eslint-disable max-lines */

import type { ITranslations } from '@/lib/i18n'
import type { IPermission } from '@/types/permissions'
import type { IAvailableIcons } from '@/components/v2/ui/icon'
import { PERMISSIONS } from '@/lib/constants/permissions'
import { ASSET_INVENTORY_URL } from '@/lib/constants/assetManager'
import { SAAS_MANAGEMENT_URL } from '@/lib/constants/employees'
import { SUPPLIER_QUESTIONNAIRES } from '@/lib/constants/supplierQuestionnaires'
import { TEXT_DOCUMENT_ROUTES } from '@/lib/constants/documents'
import { CONTEXT_DOCUMENTS_ROUTE } from '@/lib/constants/contextDocuments'
import { PUBLIC_API } from '@/lib/constants/publicApi'

interface ISidebarListItem {
  // Capability key, stored per organization: renaming one drops its saved access
  key?: string
  title: string
  url: string
  // Other pages it opens, besides its url and the pages below it
  extraUrls?: string[]
  icon?: IAvailableIcons
  items?: Omit<ISidebarListItem, 'items'>[]
  hasLink?: boolean
  permission?: IPermission
}

export interface ISidebarGroup {
  label?: string
  items: ISidebarListItem[]
}

type ISectionLabel = ITranslations['SIDEBAR']

function complianceSections(labels: ISectionLabel): Omit<ISidebarListItem, 'icon'>[] {
  return [
    { key: 'controls', title: labels.CONTROLS, url: '/compliance/controls' },
    {
      key: 'documents',
      title: labels.DOCUMENTS,
      url: '/compliance/documents',
      extraUrls: [TEXT_DOCUMENT_ROUTES.EDIT, TEXT_DOCUMENT_ROUTES.VIEW],
    },
    { key: 'evidences', title: labels.EVIDENCES, url: '/compliance/evidences' },
    { key: 'tasks', title: labels.TASKS, url: '/compliance/tasks', permission: PERMISSIONS.VIEW_TASKS },
  ]
}
function operationalGovernanceSections(labels: ISectionLabel): Omit<ISidebarListItem, 'icon'>[] {
  return [
    { key: 'assetInventory', title: labels.ASSET_INVENTORY, url: ASSET_INVENTORY_URL },
    { key: 'riskManager', title: labels.RISK_MANAGER, url: '/compliance/risk-manager' },
    { key: 'nonConformities', title: labels.NON_CONFORMITIES, url: '/compliance/non-conformities' },
  ]
}

function identitiesSections(labels: ISectionLabel): Omit<ISidebarListItem, 'icon'>[] {
  return [
    { key: 'saasManagement', title: labels.SAAS_MANAGEMENT, url: SAAS_MANAGEMENT_URL },
    { key: 'accessReview', title: labels.ACCESS_REVIEW, url: '/people/identities' },
  ]
}

function awarenessSections(labels: ISectionLabel): Omit<ISidebarListItem, 'icon'>[] {
  return [{ key: 'phishing', title: labels.PHISHING, url: '/people/phishing-campaigns' }]
}

function easmSections(labels: ISectionLabel): Omit<ISidebarListItem, 'icon'>[] {
  return [
    { key: 'dashboard', title: labels.DASHBOARD, url: '/easm/dashboard' },
    { key: 'publicDomains', title: labels.EASM_PUBLIC_DOMAINS, url: '/easm/public-domains' },
    { key: 'vulnerabilities', title: labels.EASM_VULNERABILITIES, url: '/easm/vulnerabilities' },
    { key: 'certificates', title: labels.EASM_CERTIFICATES, url: '/easm/certificates' },
    { key: 'breaches', title: labels.EASM_BREACHES, url: '/easm/breaches' },
  ]
}

function itInfrastructureSections(labels: ISectionLabel): Omit<ISidebarListItem, 'icon'>[] {
  return [
    { key: 'dashboard', title: labels.DASHBOARD, url: '/infrastructure/dashboard' },
    { key: 'vulnerabilities', title: labels.VULNERABILITIES, url: '/infrastructure/vulnerabilities' },
  ]
}

interface ISidebarOptions {
  organizationId?: number
  hasTrustCenter?: boolean
  trustCenterPath?: string
  showSiem?: boolean
  showEdr?: boolean
}

function monitoringSections(labels: ISectionLabel, options?: ISidebarOptions): Omit<ISidebarListItem, 'icon'>[] {
  const sections: Omit<ISidebarListItem, 'icon'>[] = [
    { key: 'crisisManager', title: labels.CRISIS_MANAGER, url: '/crisis-manager' },
  ]
  if (options?.showSiem) {
    sections.unshift({ title: labels.SIEM, url: '/siem' })
  } else {
    sections.unshift({ key: 'alerts', title: labels.ALERTS, url: '/infrastructure/alerts' })
  }
  if (options?.showEdr) {
    sections.unshift({ title: labels.EDR, url: '/edr' })
  }
  return sections
}

function cloudSecuritySections(labels: ISectionLabel): Omit<ISidebarListItem, 'icon'>[] {
  return [
    { key: 'dashboard', title: labels.DASHBOARD, url: '/cloud-security/dashboard' },
    { key: 'misconfigurations', title: labels.CLOUD_MISCONFIGURANTIONS, url: '/cloud-security/misconfigurations' },
  ]
}

// GRUPO PACIFICO requested that the Supplier Questionnaires section be hidden from their sidebar.
const GRUPO_PACIFICO_ORG_ID = 421

function suppliersSection(labels: ISectionLabel, organizationId?: number): Omit<ISidebarListItem, 'icon'>[] {
  const sections = [
    { key: 'dashboard', title: labels.DASHBOARD, url: '/suppliers/dashboard' },
    {
      key: 'questionnaires',
      title: labels.SUPPLIER_QUESTIONNAIRES,
      url: '/suppliers/questionnaires',
      extraUrls: [CONTEXT_DOCUMENTS_ROUTE],
    },
  ]

  if (organizationId === GRUPO_PACIFICO_ORG_ID) {
    return sections.filter((section) => section.url !== '/suppliers/questionnaires')
  }

  return sections
}

function mdmLiteSections(labels: ISectionLabel): Omit<ISidebarListItem, 'icon'>[] {
  return [
    { key: 'laptops', title: labels.LAPTOPS, url: '/assets/laptops' },
    { key: 'servers', title: labels.SERVERS, url: '/assets/servers' },
    { key: 'approvedSoftware', title: labels.APPROVED_SOFTWARE, url: '/approved-software' },
  ]
}

function mdmSections(labels: ISectionLabel): Omit<ISidebarListItem, 'icon'>[] {
  return [
    { key: 'dashboard', title: labels.DASHBOARD, url: '/mdm/dashboard' },
    { key: 'hosts', title: labels.MDM_HOSTS, url: '/mdm/hosts' },
    { key: 'groups', title: labels.MDM_GROUPS, url: '/mdm/groups' },
    { key: 'policies', title: labels.MDM_DEVICE_POLICIES, url: '/mdm/policies' },
    { key: 'software', title: labels.MDM_SOFTWARE, url: '/mdm/software' },
    { key: 'scripts', title: labels.MDM_SCRIPTS, url: '/mdm/scripts' },
  ]
}

function appsecSections(labels: ISectionLabel): Omit<ISidebarListItem, 'icon'>[] {
  return [
    { key: 'dashboard', title: labels.DASHBOARD, url: '/appsec/dashboard' },
    { key: 'sast', title: labels.APPSEC_SAST, url: '/appsec/sast' },
    { key: 'dast', title: labels.APPSEC_DAST, url: '/appsec/dast' },
    { key: 'sca', title: labels.APPSEC_SCA, url: '/appsec/sca' },
    { key: 'containersScan', title: labels.APPSEC_CONTAINER_SCAN, url: '/appsec/containers-scan' },
    { key: 'secretsDetection', title: labels.APPSEC_SECRETS, url: '/appsec/secrets-detection' },
  ]
}

// eslint-disable-next-line max-lines-per-function
export default function getSidebarSections(labels: ISectionLabel, options?: ISidebarOptions): ISidebarGroup[] {
  const operationsItems: ISidebarListItem[] = [
    { title: labels.CONNECTORS, icon: 'power-outlined', url: '/connectors' },
    { key: PUBLIC_API.CAPABILITY_KEY, title: labels.API, icon: 'terminal-outlined', url: PUBLIC_API.PAGE_URL },
    {
      title: labels.EMPLOYEES_CENTER,
      icon: 'briefcase-outlined',
      url: '/employee-center',
    },
  ]
  const trustCenterPath = options?.trustCenterPath
  const hasTrustCenter = options && options.hasTrustCenter && trustCenterPath
  if (hasTrustCenter) {
    operationsItems.push({
      title: labels.TRUST_CENTER,
      icon: 'shield-check',
      url: trustCenterPath,
      permission: PERMISSIONS.VIEW_TRUST_CENTER,
    })
  }
  operationsItems.push({
    title: labels.BACKOFFICE,
    icon: 'wrench-outlined',
    url: '/backoffice',
    permission: PERMISSIONS.VIEW_BACKOFFICE,
    items: [
      ...(options?.organizationId
        ? [
            {
              title: labels.ORGANIZATION,
              url: `/backoffice/organizations/${options.organizationId}`,
            } satisfies ISidebarListItem,
          ]
        : []),
      { title: labels.CUSTOMER_SUCCESS, url: '/backoffice/customer-success' },
      { title: labels.SCANS, url: '/backoffice/scans' },
      { title: labels.COMPLIANCE_DATA, url: '/backoffice/compliance-data' },
      { title: labels.STANDARD_EDITOR, url: '/backoffice/standard-editor' },
      { title: labels.SUPPLIER_QUESTIONNAIRES, url: SUPPLIER_QUESTIONNAIRES.BACKOFFICE_PATH },
      { title: labels.PUBLIC_API, url: PUBLIC_API.BACKOFFICE_PAGE_URL, permission: PERMISSIONS.VIEW_ALL_TENANTS },
      { title: labels.CHAT_LOGS, url: '/backoffice/chat-logs', permission: PERMISSIONS.VIEW_CHAT_LOGS },
    ],
  })
  return [
    {
      items: [{ key: 'home', title: labels.HOME, icon: 'home-outlined', url: '/' }],
    },
    {
      label: labels.GOVERNANCE,
      items: [
        {
          key: 'compliance',
          title: labels.COMPLIANCE,
          icon: 'book-outlined',
          url: '/compliance',
          hasLink: false,
          items: complianceSections(labels),
        },
        {
          key: 'operationalGovernance',
          title: labels.OPERATIONAL_GOVERNANCE,
          icon: 'workflow',
          url: '/suppliers/dashboard',
          hasLink: false,
          items: operationalGovernanceSections(labels),
        },
        {
          key: 'suppliers',
          title: labels.SUPPLIERS,
          icon: 'package',
          url: '/suppliers',
          hasLink: false,
          items: suppliersSection(labels, options?.organizationId),
        },
      ],
    },
    {
      label: labels.ORGANIZATION,
      items: [
        {
          key: 'employeeDashboard',
          title: labels.ORGANIZATION_EMPLOYEE_DASHBOARD,
          icon: 'users-round',
          url: '/people/employee-dashboard',
        },
        { key: 'itInventory', title: labels.IT_INVENTORY, icon: 'layers', url: '/it-inventory' },
        {
          key: 'identities',
          title: labels.IDENTITIES,
          icon: 'fingerprint',
          url: '/identities',
          hasLink: false,
          items: identitiesSections(labels),
        },
        {
          key: 'awareness',
          title: labels.AWARENESS,
          icon: 'graduation-cap',
          url: '/awareness',
          hasLink: false,
          items: awarenessSections(labels),
        },
      ],
    },
    {
      label: labels.SECURITY,
      items: [
        {
          key: 'easm',
          title: labels.EASM,
          icon: 'public-outlined',
          url: '/easm',
          items: easmSections(labels),
        },
        {
          key: 'infrastructure',
          title: labels.INFRASTRUCTURE,
          icon: 'dns-outlined',
          url: '/infrastructure',
          hasLink: false,
          items: itInfrastructureSections(labels),
        },
        {
          key: 'cloudSecurity',
          title: labels.CLOUD_SECURITY,
          icon: 'cloud-done-outlined',
          url: '/cloud-security',
          items: cloudSecuritySections(labels),
        },
        {
          key: 'appsec',
          title: labels.APPSEC,
          icon: 'terminal-outlined',
          url: '/appsec',
          items: appsecSections(labels),
        },
        {
          key: 'monitoring',
          title: labels.MONITORING,
          icon: 'crisis-alert',
          url: '/infrastructure/endpoint-monitoring',
          hasLink: false,
          items: monitoringSections(labels, options),
        },
        {
          key: 'mdmLite',
          title: labels.MDM_LITE,
          icon: 'tablet-smartphone',
          url: '/mdm-lite',
          hasLink: false,
          items: mdmLiteSections(labels),
        },
        {
          key: 'mdm',
          title: labels.MDM,
          icon: 'devices-outlined' as const,
          url: '/mdm',
          hasLink: false,
          permission: PERMISSIONS.VIEW_MDM,
          items: mdmSections(labels),
        },
      ],
    },
    {
      label: labels.OPERATIONS,
      items: operationsItems,
    },
  ]
}
